/**
 * Diálogo de admin que publica una novedad del sistema a todos los usuarios de su organización.
 * Muestra una vista previa con el mismo componente que usa la tarjeta Novedades.
 */
import { useStore } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FormDialogContent } from '@/components/ui/form-dialog-content'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { getFieldError } from '@/components/clientes/wizard/useClienteForm'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { notificacionesService } from '@/core/services/notificacionesService'

import { usePublicarNovedadForm } from './forms/usePublicarNovedadForm'
import { NovedadSistemaItem } from './NovedadSistemaItem'

interface PublicarNovedadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PublicarNovedadDialog({ open, onOpenChange }: PublicarNovedadDialogProps) {
  const { supabase } = useSupabase()
  const { organizacionId, usuario } = useOrganizacion()
  const { esAdmin } = usePermissions()

  const { form } = usePublicarNovedadForm({
    onSubmit: async (values) => {
      // El email sale de usuarios_brainhub: notificar() lo compara contra current_user_email().
      if (!esAdmin || !supabase || !organizacionId || !usuario?.email) {
        toast.error('Solo un administrador puede publicar novedades')
        return
      }
      try {
        await notificacionesService.publicarNovedadSistema(supabase, {
          organizacionId,
          actorEmail: usuario.email,
          titulo: values.titulo.trim(),
          mensaje: values.mensaje.trim(),
        })
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'No se pudo publicar la novedad')
        return
      }
      toast.success('Novedad publicada para tu organización')
      form.reset()
      onOpenChange(false)
    },
  })

  const isDirty = useStore(form.store, (s) => s.isDirty)
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting)
  const titulo = useStore(form.store, (s) => s.values.titulo)
  const mensaje = useStore(form.store, (s) => s.values.mensaje)

  // Con la publicación en vuelo no se cierra: el insert sigue y el diálogo desaparecería sin aviso.
  const cerrar = () => {
    if (isSubmitting) return
    form.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : cerrar())}>
      <FormDialogContent isDirty={isDirty} onDiscard={cerrar} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Publicar novedad</DialogTitle>
          <DialogDescription>
            Le llega a todos los usuarios de tu organización en Novedades y en sus notificaciones.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field name="titulo">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="novedad-titulo">Título</Label>
                <Input
                  id="novedad-titulo"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  maxLength={80}
                  placeholder="Ej: Nuevo reporte de deuda por cliente"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="mensaje">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="novedad-mensaje">Texto</Label>
                <Textarea
                  id="novedad-mensaje"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  maxLength={500}
                  rows={4}
                  placeholder="Contá en pocas líneas qué cambió"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                )}
              </div>
            )}
          </form.Field>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Vista previa
            </p>
            <div className="rounded-md border p-3">
              <NovedadSistemaItem
                titulo={titulo.trim() || 'Título de la novedad'}
                mensaje={mensaje.trim() || 'Texto de la novedad'}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={cerrar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publicar
            </Button>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
